import { BehaviorSubject, lastValueFrom, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BaseService } from 'src/app/shared/services/base.service';
import {
  CredentialDefinitionModel,
  CredentialDefinitionType
} from '../../../shared/models/credential/credential-definition.model';
import { ApiVersions } from 'src/app/shared/services/api-version';
import { ScenarioDefinitionModel } from './admin-credential-scenario/scenario-definition-model';
import {
  ICredentialFeeDefinitionModel,
  ICredentialFeeDefinitionWithIdModel,
  IScenarioRequirementBase,
  ScenarioRequirementDefinition
} from 'src/app/shared/models/credential/scenarioRequirementDefinition.model';
import { EvidenceDefinition } from 'src/app/shared/models/credential/evidenceDefinition.model';
import {
  EvidenceAutomationActionType,
  RequirementAutomation
} from './admin-credential-scenario/admin-credential-scenario-details/admin-requirement-side-bar/evidence-automation-condition-item';
import { CredentialApplicationAutoCloseRuleModel } from './admin-credential-application/admin-credential-application-auto-close-sidebar/credential-application-auto-close-rule.model';

import { CredentialEmailConfigurationModel } from 'src/app/shared/models/credential/credentialEmailConfiguration.model';
import {
  ActiveOrInactive,
  CredentialEmailModel
} from 'src/app/shared/models/credential/credentialEmail.model';
import { CredentialApplicationDefinitionModel } from 'src/app/shared/models/credential/credential-application-definition.model';
import { CredentialApplicationDefinitionType } from 'src/app/shared/models/credential/CredentialApplicationDefinitionType';
import {
  CredentialSpecialtyDefinitionModel,
  CredentialSpecialtyDefinitionStatus
} from 'src/app/admin/admin-home/admin-credentials/admin-credential-specialty/credential-specialty-definition.model';
import { AdminSpecialtySettingModel } from './admin-credential-application/admin-specialty-settings/admin-specialty-setting.model';
import { CredentialDefinitionUsageReport } from 'src/app/shared/models/credential/credentialDefinitionUsageReport';
import { CredentialPrimaryRenewalModel } from 'src/app/shared/models/credential/credentialPrimaryRenewal.model';
import { CustomCredentialStatusResultModel } from 'src/app/shared/models/credential/customCredentialStatusResultModel.model';
import { CustomPersonCredentialApplicationStatusDefinition } from 'src/app/shared/models/credential/customPersonCredentialApplicationStatusDefinition.model';
import { CredentialApplicationStatusesReportModel } from 'src/app/shared/models/credential/credential-application-statuses.model';
import { getParsedError } from 'src/app/shared/services/error-handler-factory.service';

@Injectable({
  providedIn: 'root'
})
export class AdminCredentialsService extends BaseService {
  private _credentials: CredentialDefinitionModel[] = [];
  private _credentialsLoaded = false;
  private _credentialsInFlight: Promise<CredentialDefinitionModel[]> | null = null;
  private _specialties: CredentialSpecialtyDefinitionModel[] = [];
  private _credentialNamesCache: Map<string, string> | null = null;
  private _credentialTypeCache: Map<string, CredentialDefinitionType> | null = null;

  private _credentialNamesCacheInFlight: Promise<
    { id: string; name: string; type: CredentialDefinitionType }[]
  > | null = null;

  public readonly credentials: BehaviorSubject<CredentialDefinitionModel[]> = new BehaviorSubject<
    CredentialDefinitionModel[]
  >(undefined);
  public readonly credential: BehaviorSubject<CredentialDefinitionModel> =
    new BehaviorSubject<CredentialDefinitionModel>(undefined);

  public readonly specialties: BehaviorSubject<CredentialSpecialtyDefinitionModel[]> =
    new BehaviorSubject<CredentialSpecialtyDefinitionModel[]>([]);

  public readonly scenario: BehaviorSubject<ScenarioDefinitionModel> =
    new BehaviorSubject<ScenarioDefinitionModel>(null);

  public readonly primaryRenewal: BehaviorSubject<CredentialPrimaryRenewalModel> =
    new BehaviorSubject<CredentialPrimaryRenewalModel>(null);

  constructor() {
    super();
  }

  getAllCredentials(): Promise<CredentialDefinitionModel[]> {
    if (this._credentialsLoaded) {
      return Promise.resolve(this._credentials);
    }
    if (this._credentialsInFlight) {
      return this._credentialsInFlight;
    }
    this._credentialsInFlight = lastValueFrom(
      this.get(this.credentialsBaseUrl, false, ApiVersions.v1_0).pipe(
        map(this.convertCredentialsData)
      )
    )
      .then((result) => {
        this._credentials = result.sort((a, b) => a.name.localeCompare(b.name));
        this._credentialsLoaded = true;
        this.credentials.next(this._credentials);
        return result;
      })
      .finally(() => {
        this._credentialsInFlight = null;
      });
    return this._credentialsInFlight;
  }

  async getCredentialName(id: string): Promise<string> {
    if (this._credentialNamesCache?.has(id)) {
      return this._credentialNamesCache.get(id);
    }

    const refreshed = await this.refreshCredentialCache();
    this._credentialNamesCache = new Map(refreshed.map((n) => [n.id, n.name]));
    if (this._credentialNamesCache.has(id)) {
      return this._credentialNamesCache.get(id);
    }
    return ''; //is was not found in refresh.
  }

  async getCredentialType(id: string): Promise<CredentialDefinitionType> {
    if (this._credentialTypeCache?.has(id)) {
      return this._credentialTypeCache.get(id);
    }
    const refreshed = await this.refreshCredentialCache();
    this._credentialTypeCache = new Map(refreshed.map((n) => [n.id, n.type]));

    return this._credentialTypeCache.get(id);
  }

  private refreshCredentialCache = async () => {
    if (!this._credentialNamesCacheInFlight) {
      this._credentialNamesCacheInFlight = this.getAllCredentialsNames().finally(() => {
        this._credentialNamesCacheInFlight = null;
      });
    }
    return this._credentialNamesCacheInFlight;
  };

  getAllCredentialsNames(
    type?: CredentialDefinitionType
  ): Promise<{ id: string; name: string; type: CredentialDefinitionType }[]> {
    return lastValueFrom(
      this.get(`${this.credentialsBaseUrl}/names`, false, ApiVersions.v1_0).pipe(
        map(this.convertCredentialsData)
      )
    ).then((result) => {
      return result
        .filter((credential) => !type || credential.type === type)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((credential) => ({
          id: credential.id,
          name: credential.name,
          type: credential.type
        }));
    });
  }

  getAllApplicationStatusLabels = (): Promise<string[]> => {
    return lastValueFrom(
      this.get<string[]>(
        `${this.credentialsBaseUrl}/all-application-status-labels`,
        true,
        ApiVersions.v1_0
      )
    );
  };

  getInProgressCustomCredentialApplicationStatuses = (): Promise<
    CustomPersonCredentialApplicationStatusDefinition[]
  > => {
    return lastValueFrom(
      this.get<CustomPersonCredentialApplicationStatusDefinition[]>(
        `${this.credentialsBaseUrl}/custom-statuses/in-progress`,
        true,
        ApiVersions.v0_1
      )
    );
  };

  getAllApplicationReportStatuses = (
    credentialDefinitionId: string
  ): Promise<CredentialApplicationStatusesReportModel> => {
    const url = credentialDefinitionId
      ? `${this.credentialsBaseUrl}/${credentialDefinitionId}/reports-application-status`
      : `${this.credentialsBaseUrl}/reports-application-status`;
    return lastValueFrom(
      this.get(url, true, ApiVersions.v1_0).pipe(
        map((data) => new CredentialApplicationStatusesReportModel(data))
      )
    );
  };

  getAllStatuses = (credentialDefinitionId: string): Promise<CustomCredentialStatusResultModel> =>
    lastValueFrom(
      this.get<CustomCredentialStatusResultModel>(
        `${this.credentialsBaseUrl}/${credentialDefinitionId}/custom-statuses`,
        true,
        ApiVersions.v0_1
      )
    );

  getCustomRules = (credentialId: string): Promise<string[]> =>
    lastValueFrom(
      this.get(`${this.credentialsBaseUrl}/${credentialId}/custom-rules`, true, ApiVersions.v1_0)
    );

  getCredential(credentialId: string): void {
    let path = this.credentialsBaseUrl.concat('/', credentialId);

    this.get(path, true, ApiVersions.v1_0)
      .pipe(map((data) => new CredentialDefinitionModel(data)))
      .subscribe((result) => {
        this.credential.next(result);
      });
  }

  getUsageReport = async (
    credential: CredentialDefinitionModel
  ): Promise<CredentialDefinitionUsageReport> => {
    return await lastValueFrom(
      this.get<CredentialDefinitionUsageReport>(
        `${this.credentialsBaseUrl}/${credential.id}/usage`,
        true,
        ApiVersions.v0_1
      ).pipe(map((result) => new CredentialDefinitionUsageReport(result)))
    );
  };

  createCredential = (value: CredentialDefinitionModel): Promise<CredentialDefinitionModel> =>
    lastValueFrom(this.post(this.credentialsBaseUrl, value, null, ApiVersions.v1_0)).then((res) => {
      if (!res) {
        return null;
      }
      const credential = new CredentialDefinitionModel(res);
      this._credentials.push(credential);
      this.credentials.next(this._credentials);
      this.openSnackBar(`${value.name} was created successfully`, true);
      this.router.navigate(['/admin/credentials', credential.id]);
      return credential;
    });

  updateCredential = (credential: CredentialDefinitionModel) =>
    this.put(
      `${this.credentialsBaseUrl}/${credential.id}`,
      JSON.stringify(credential),
      null,
      ApiVersions.v1_0
    ).pipe(
      map((res) => {
        if (res) {
          let credential = new CredentialDefinitionModel(res);
          let index = this._credentials.findIndex((x) => x.id === credential.id);
          this._credentials[index] = credential;
          this.credentials.next(this._credentials);
          this.credential.next(credential);
          this.openSnackBar('Credential updated successfully', true);
          return true;
        }
        return false;
      })
    );

  setPrimaryRenewalForm = (
    credentialApplicationDefinitionId: string,
    primaryRenewalFormId?: string
  ) => {
    const model = JSON.stringify({ formId: primaryRenewalFormId });
    const path = `credential-applications/${credentialApplicationDefinitionId}/primary-renewal-form`;
    return lastValueFrom(this.put(path, model, null, ApiVersions.v1_0, true)).then((data) => {
      if (data) {
        const primaryRenewalForm = new CredentialPrimaryRenewalModel(data);
        this.primaryRenewal.next(primaryRenewalForm);
        return primaryRenewalForm;
      }
      return null;
    });
  };

  getPrimaryRenewalForm = (
    credentialApplicationDefinitionId: string
  ): Promise<CredentialPrimaryRenewalModel> => {
    const path = `credential-applications/${credentialApplicationDefinitionId}/primary-renewal-form`;
    return lastValueFrom(
      this.get(path, true, ApiVersions.v1_0).pipe(
        map((data) => new CredentialPrimaryRenewalModel(data))
      )
    );
  };

  archiveCredential = (credential: CredentialDefinitionModel): Observable<boolean> =>
    this.post(
      `${this.credentialsBaseUrl}/${credential.id}/archive`,
      null,
      null,
      ApiVersions.v0_1
    ).pipe(
      map((res) => {
        if (res) {
          let credential = new CredentialDefinitionModel(res);
          let index = this._credentials.findIndex((x) => x.id === credential.id);
          this._credentials[index] = credential;
          this.credentials.next(this._credentials);
          this.credential.next(credential);
          this.openSnackBar('Credential archived successfully', true);
          return true;
        }
        return false;
      })
    );

  deleteCredential(credential: CredentialDefinitionModel): void {
    let path = this.credentialsBaseUrl.concat('/', credential.id);
    this.delete(path, null, ApiVersions.v1_0).subscribe(() => {
      this._credentials = this._credentials.filter((x) => x.id !== credential.id);
      this.credentials.next(this._credentials);
      this.openSnackBar(`${credential.name} was deleted successfully`, true);
    });
  }

  getCredentialApplication(
    credentialApplicationId: string
  ): Promise<CredentialApplicationDefinitionModel> {
    let path = `credential-applications/${credentialApplicationId}`;

    return lastValueFrom(
      this.get(path, true, ApiVersions.v1_0).pipe(
        map((data) => new CredentialApplicationDefinitionModel(data))
      )
    );
  }

  addCredentialApplication(
    credentialId: string,
    model: any,
    type: CredentialApplicationDefinitionType
  ): Promise<any> {
    let path =
      type == CredentialApplicationDefinitionType.Initial
        ? this.credentialsBaseUrl.concat('/', credentialId, '/applications/initial')
        : type == CredentialApplicationDefinitionType.Renewal
          ? this.credentialsBaseUrl.concat('/', credentialId, '/applications/renewal')
          : type == CredentialApplicationDefinitionType.Reinstatement
            ? this.credentialsBaseUrl.concat('/', credentialId, '/applications/reinstatement')
            : this.credentialsBaseUrl.concat('/', credentialId, '/applications/specialty');
    return lastValueFrom(
      this.post(path, model, null, ApiVersions.v1_0).pipe(
        map((data) => {
          if (data) {
            let credential = new CredentialDefinitionModel(data);
            let index = this._credentials.findIndex((x) => x.id === credential.id);
            this._credentials[index] = credential;
            this.credentials.next(this._credentials);
            this.credential.next(credential);
            this.openSnackBar(`${type} application was added successfully`, true);

            this.router.navigate(['/admin/credentials', credential.id, 'application', type]);
            return true;
          }
        })
      )
    );
  }

  getScenario = (credentialId: string, scenarioId: string): Promise<ScenarioDefinitionModel> =>
    lastValueFrom(
      this.get(
        `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}`,
        false,
        ApiVersions.v1_0
      )
    ).then((data) => {
      const scenario = new ScenarioDefinitionModel(data);
      this.scenario.next(scenario);
      return scenario;
    });

  addScenario(
    credentialId: string,
    value: Partial<ScenarioDefinitionModel>
  ): Promise<ScenarioDefinitionModel> {
    let path = this.credentialsBaseUrl.concat('/', credentialId, '/scenarios');
    return lastValueFrom(this.post(path, value, null, ApiVersions.v1_0)).then((res) => {
      if (!res) {
        return null;
      }
      const scenario = new ScenarioDefinitionModel(res);
      this.openSnackBar(`${scenario.name} was added successfully`, true);
      this.getCredential(credentialId);
      return scenario;
    });
  }

  updateScenario = (scenario: ScenarioDefinitionModel): Promise<ScenarioDefinitionModel> =>
    lastValueFrom(
      this.put(
        `${this.credentialsBaseUrl}/${scenario.credentialDefinitionId}/scenarios/${scenario.id}/`,
        JSON.stringify(scenario),
        null,
        ApiVersions.v1_0
      )
    ).then((res) => {
      if (!res) {
        return null;
      }
      this.getCredential(scenario.credentialDefinitionId);
      this.scenario.next(scenario);
      return new ScenarioDefinitionModel(res);
    });

  async deleteScenario(scenario: ScenarioDefinitionModel): Promise<boolean> {
    let path = this.credentialsBaseUrl.concat(
      '/',
      scenario.credentialDefinitionId,
      '/scenarios/',
      scenario.id
    );
    const isSuccessful = await lastValueFrom(this.delete(path, null, ApiVersions.v1_0, false));
    if (isSuccessful) {
      this.getCredential(scenario.credentialDefinitionId);
      this.openSnackBar(`${scenario.name} was deleted successfully`, true);
    }
    return isSuccessful;
  }

  async cloneScenario(
    credentialId: string,
    scenarioId: string,
    type: CredentialApplicationDefinitionType
  ): Promise<ScenarioDefinitionModel | false> {
    const clonedScenario = (await lastValueFrom(
      this.post(
        `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/clone`,
        JSON.stringify({ type }),
        null,
        ApiVersions.v1_0,
        true
      )
    )) as ScenarioDefinitionModel;
    if (!clonedScenario) {
      return false;
    }

    this.openSnackBar(`Scenario was cloned successfully`, true);
    this.getCredential(credentialId);
    return clonedScenario;
  }

  updateCredentialFee = (
    credentialId: string,
    credentialFeeDefinitionId: string,
    model: ICredentialFeeDefinitionModel
  ): Promise<ICredentialFeeDefinitionModel> =>
    lastValueFrom(
      this.put(
        `${this.credentialsBaseUrl}/${credentialId}/fees/${credentialFeeDefinitionId}`,
        JSON.stringify(model),
        null,
        ApiVersions.v1_0
      )
    );

  deleteRequirement = async (
    credentialId: string,
    scenarioId: string,
    requirementId: string
  ): Promise<boolean> =>
    lastValueFrom(
      this.delete(
        `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}`,
        null,
        ApiVersions.v1_0
      )
    ) as Promise<boolean>;

  /** note: parseRequirementResult will refresh the scenario after creating. no need to add new requirement to scenario.requirements */
  createRequirement(
    credentialId: string,
    scenarioId: string,
    value: IScenarioRequirementBase & { feeDefinitionId?: string }
  ): Promise<ScenarioRequirementDefinition> {
    return lastValueFrom(
      this.post(
        `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements`,
        value,
        null,
        ApiVersions.v1_0
      )
    ).then((jsonResult) => this.parseRequirementResult(jsonResult, credentialId, scenarioId));
  }

  updateRequirement(
    credentialId: string,
    scenarioId: string,
    requirementId: string,
    value: IScenarioRequirementBase
  ): Promise<ScenarioRequirementDefinition> {
    return lastValueFrom(
      this.put(
        `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements`,
        JSON.stringify({
          ...value,
          id: requirementId
        }),
        null,
        ApiVersions.v1_0
      )
    ).then((jsonResult) => this.parseRequirementResult(jsonResult, credentialId, scenarioId));
  }

  private parseRequirementResult = async (jsonResult, credentialId: string, scenarioId: string) => {
    if (!jsonResult) {
      return; //API error
    }
    //a hack trigger 'this.scenario.next(scenario)' with a refreshed scenario;
    //without this; editing requirement name or subscription won't show up without page refreshing
    //alternatively we could keep track of the scenario model, edit it then call this.scenario.next(scenario) without an API call
    await this.getScenario(credentialId, scenarioId);

    return ScenarioRequirementDefinition.parse(jsonResult);
  };

  addOrUpdateRequirementAutomation(
    credentialId: string,
    scenarioId: string,
    requirementId: string,
    value: RequirementAutomation
  ): Promise<RequirementAutomation[]> {
    return lastValueFrom(
      this.post(
        `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}/automations`,
        JSON.stringify(value),
        null,
        ApiVersions.v1_0
      ).pipe(map((jsonResult) => (jsonResult ? (jsonResult as RequirementAutomation[]) : null)))
    );
  }

  deleteRequirementAutomation(
    credentialId: string,
    scenarioId: string,
    requirementId: string,
    automation: RequirementAutomation
  ): Promise<boolean> {
    return lastValueFrom(
      this.delete(
        `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}/automations/${automation.id}`,
        null,
        ApiVersions.v1_0,
        true
      ).pipe(
        map(() => {
          return true;
        }),
        catchError((error) => {
          this.openSnackBar(error.error, false);
          return of(false);
        })
      )
    );
  }

  getRequirementAutomations = (
    credentialId: string,
    scenarioId: string,
    requirementId: string,
    automationActionType: EvidenceAutomationActionType
  ): Promise<RequirementAutomation[]> =>
    lastValueFrom(
      this.get(
        `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}/automations/${automationActionType}`,
        null,
        ApiVersions.v1_0
      )
    );

  createEvidence(
    credentialId: string,
    scenarioId: string,
    requirementId: string,
    value: EvidenceDefinition
  ): Promise<EvidenceDefinition> {
    return lastValueFrom(
      this.post(
        `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}/evidences/`,
        value,
        null,
        ApiVersions.v1_0
      )
    );
  }

  deleteEvidence = async (
    credentialId: string,
    scenarioId: string,
    requirementId: string,
    evidenceId
  ): Promise<boolean> => {
    try {
      await lastValueFrom(
        this.delete(
          `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}/evidences/${evidenceId}`,
          null,
          ApiVersions.v1_0,
          true
        )
      );
      return true;
    } catch (error) {
      this.openSnackBar(getParsedError(error).message, false);
      return false;
    }
  };

  getEvidences = (
    credentialId: string,
    scenarioId: string,
    requirementId: string
  ): Promise<{
    evidences: EvidenceDefinition[];
    credentialFeeDefinition: ICredentialFeeDefinitionWithIdModel;
  }> => {
    if (!requirementId) {
      throw new Error('requirementId is required to get evidences');
    }
    return lastValueFrom(
      this.get(
        `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}/evidences/`,
        null,
        ApiVersions.v1_0
      )
    ).then((result) => {
      if (result.baseAmount) {
        return {
          evidences: null,
          credentialFeeDefinition: result as ICredentialFeeDefinitionWithIdModel
        };
      }
      return {
        evidences: result as EvidenceDefinition[],
        credentialFeeDefinition: null
      };
    });
  };

  createSpecialtyEvidence(
    credentialId: string,
    value: EvidenceDefinition
  ): Promise<EvidenceDefinition> {
    return lastValueFrom(
      this.post(
        `${this.credentialsBaseUrl}/${credentialId}/specialties/evidences/`,
        value,
        null,
        ApiVersions.v1_0
      )
    );
  }

  editSpecialtyEvidence(
    credentialId: string,
    value: EvidenceDefinition
  ): Promise<EvidenceDefinition> {
    return lastValueFrom(
      this.put(
        `${this.credentialsBaseUrl}/${credentialId}/specialties/evidences/${value.id}/`,
        JSON.stringify(value),
        null,
        ApiVersions.v1_0
      )
    );
  }

  async deleteSpecialtyEvidence(credentialId: string, evidenceId: string): Promise<boolean> {
    try {
      await lastValueFrom(
        this.delete(
          `${this.credentialsBaseUrl}/${credentialId}/specialties/evidences/${evidenceId}/`,
          null,
          ApiVersions.v1_0,
          true
        )
      );

      return true;
    } catch (error) {
      this.openSnackBar(getParsedError(error).message, false);

      return false;
    }
  }

  getSpecialtyEvidences = (credentialId: string): Promise<EvidenceDefinition[]> =>
    lastValueFrom(
      this.get(
        `${this.credentialsBaseUrl}/${credentialId}/specialties/evidences/`,
        null,
        ApiVersions.v1_0
      )
    ).then((result) => {
      return result as EvidenceDefinition[];
    });

  updateCredentialApplicationAutoCloseRule(
    application: CredentialApplicationDefinitionModel,
    value: CredentialApplicationAutoCloseRuleModel
  ): Promise<CredentialApplicationAutoCloseRuleModel> {
    const path = `credential-applications/${application.id}/auto-close-rule`;
    const body = JSON.stringify(value);
    return lastValueFrom(this.post(path, body, null, ApiVersions.v1_0));
  }

  deleteCredentialApplicationAutoCloseRule(
    application: CredentialApplicationDefinitionModel
  ): Promise<boolean> {
    const path = `credential-applications/${application.id}/auto-close-rule`;
    return lastValueFrom(
      this.delete(path, null, ApiVersions.v1_0, true).pipe(
        map(() => {
          return true;
        }),
        catchError((error) => {
          this.openSnackBar(error.error, false);
          return of(false);
        })
      )
    );
  }

  rankEvidences = (
    credentialId: string,
    scenarioId: string,
    requirementId: string,
    evidenceOrder: string[]
  ): Promise<boolean> =>
    lastValueFrom(
      this.patch(
        `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}/evidences/rank`,
        JSON.stringify(evidenceOrder),
        ApiVersions.v1_0
      ).pipe(map((jsonResult) => !!jsonResult)) //return will be null on error, true on success
    );

  getCredentialEmailConfig = (id: string): Promise<CredentialEmailConfigurationModel> =>
    lastValueFrom(
      this.get(`${this.credentialsBaseUrl}/${id}/emails`, true, ApiVersions.v1_0).pipe(
        map((data) => CredentialEmailConfigurationModel.parse(data))
      )
    );

  /** returns null on error */
  addOrUpdateCredentialEmail = async (
    credentialId: string,
    email: CredentialEmailModel
  ): Promise<CredentialEmailModel> => {
    try {
      return await lastValueFrom(
        this.post(
          `${this.credentialsBaseUrl}/${credentialId}/emails`,
          JSON.stringify(email),
          null,
          ApiVersions.v0_1,
          true
        )
      );
    } catch (error) {
      this.openSnackBar(getParsedError(error).message, false);
      return null;
    }
  };

  removeAttachment = async (
    credentialId: string,
    credentialEmailId: string,
    attachmentId: string
  ): Promise<boolean> => {
    try {
      return await lastValueFrom(
        this.delete(
          `${this.credentialsBaseUrl}/${credentialId}/emails/${credentialEmailId}/${attachmentId}`,
          null,
          ApiVersions.v0_1,
          true
        )
      );
    } catch (error) {
      this.openSnackBar(getParsedError(error).message, false);
      return false;
    }
  };

  updateCredentialEmailStatus(
    credentialId: string,
    credentialEmailId: string,
    newStatus: ActiveOrInactive
  ): Promise<CredentialEmailModel> {
    return lastValueFrom(
      this.patch(
        `${this.credentialsBaseUrl}/${credentialId}/emails/${credentialEmailId}`,
        JSON.stringify(newStatus),
        ApiVersions.v0_1
      ).pipe(map((jsonResult) => (jsonResult ? (jsonResult as CredentialEmailModel) : null)))
    );
  }

  addCredentialSpecialty(
    credentialDefinitionId: string,
    credentialSpecialtyDefinition: CredentialSpecialtyDefinitionModel
  ) {
    let path = `credential-specialties/${credentialDefinitionId}`;

    this.post(
      `${path}`,
      JSON.stringify(credentialSpecialtyDefinition),
      null,
      ApiVersions.v1_0,
      true
    ).subscribe((res) => {
      if (res) {
        let credentialSpecialty = new CredentialSpecialtyDefinitionModel(res);
        this._specialties.push(credentialSpecialty);
        this.specialties.next(this._specialties);
        this.openSnackBar(`${credentialSpecialtyDefinition.name} was added successfully`, true);
      }
    });
  }

  updateSpecialty(
    credentialDefinitionId: string,
    credentialSpecialtyDefinitionId: string,
    credentialSpecialtyDefinitionModel: CredentialSpecialtyDefinitionModel
  ) {
    let path = `credential-specialties/${credentialDefinitionId}/${credentialSpecialtyDefinitionId}`;

    const body = JSON.stringify(credentialSpecialtyDefinitionModel);

    this.put(path, body, null, ApiVersions.v1_0).subscribe(
      (res) => {
        if (res) {
          let credentialSpecialty = new CredentialSpecialtyDefinitionModel(res);
          let index = this._specialties.findIndex((x) => x.id === credentialSpecialty.id);
          this._specialties[index] = credentialSpecialty;
          this.specialties.next(this._specialties);
          this.openSnackBar(
            `${credentialSpecialtyDefinitionModel.name} was updated successfully`,
            true
          );
        }
      },
      (error) => {
        this.openSnackBar(getParsedError(error).message, false);
      }
    );
  }

  deleteSpecialty(credentialDefinitionId: string, credentialSpecialtyDefinitionId: string) {
    this.delete(
      `credential-specialties/${credentialDefinitionId}/${credentialSpecialtyDefinitionId}`,
      null,
      ApiVersions.v1_0,
      true
    ).subscribe(() => {
      this._specialties = this._specialties.filter((x) => x.id !== credentialSpecialtyDefinitionId);
      this.specialties.next(this._specialties);
      this.openSnackBar(`Specialty was deleted successfully`, true);
    });
  }

  getAllSpecialties(type: CredentialDefinitionType): Promise<CredentialSpecialtyDefinitionModel[]> {
    return lastValueFrom(this.get(`credential-specialties/${type}`, null, ApiVersions.v1_0));
  }

  getSpecialties(credentialDefinitionId: string): void {
    this.get(`credential-specialties/${credentialDefinitionId}`, null, ApiVersions.v1_0).subscribe(
      (result) => {
        this._specialties = result;
        this.specialties.next(this._specialties);
      }
    );
  }

  updateSpecialtyStatus(
    credentialDefinitionId: string,
    credentialSpecialtyDefinitionId: string,
    CredentialSpecialtyDefinitionStatus: CredentialSpecialtyDefinitionStatus
  ) {
    let path = `credential-specialties/${credentialDefinitionId}/${credentialSpecialtyDefinitionId}`;
    const body = JSON.stringify(CredentialSpecialtyDefinitionStatus);
    this.patch(path, body, ApiVersions.v1_0).subscribe((res) => {
      if (res) {
        let credentialSpecialty = new CredentialSpecialtyDefinitionModel(res);
        let index = this._specialties.findIndex((x) => x.id === credentialSpecialty.id);
        this._specialties[index] = credentialSpecialty;
        this.specialties.next(this._specialties);
        this.openSnackBar(`Status was updated successfully`, true);
      }
    });
  }

  addOrUpdateSpecialtySetting(
    applicationId: string,
    value: AdminSpecialtySettingModel
  ): Promise<AdminSpecialtySettingModel> {
    const path = `credential-applications/${applicationId}/specialty-setting`;
    const body = JSON.stringify(value);
    return lastValueFrom(this.post(path, body, null, ApiVersions.v1_0));
  }

  private get credentialsBaseUrl(): string {
    return 'credential-definition';
  }

  private convertCredentialsData(data: CredentialDefinitionModel[]) {
    if (!data) {
      return [];
    }
    return data.map((credential) => new CredentialDefinitionModel(credential));
  }
}
